import { userService } from "@/services/user/user.service";
import { InviteUserDto, PageableSearchOptions } from "@exibra/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isNil from "lodash.isnil";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const userQueryKeys = {
    userData: "user-data",
    users: (tenantId: number, options?: PageableSearchOptions) =>
        [`users-${tenantId}`, options?.page, options?.pageSize, options?.search] as const,
} as const;

export const useUserData = () => {
    return useQuery({
        queryKey: [userQueryKeys.userData],
        queryFn: () => userService.getUserData(),
        refetchOnMount: false,
    });
};

export const useUsers = (tenantId?: number, options: PageableSearchOptions = {}) => {
    return useQuery({
        queryKey: [userQueryKeys.users(tenantId!, options)],
        queryFn: () => userService.getUsers(tenantId!, options),
        enabled: !isNil(tenantId),
    });
};

export const useInviteUser = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (params: InviteUserDto) => userService.inviteUser(params),
        onSuccess: (_, params) => {
            queryClient.invalidateQueries({
                queryKey: [userQueryKeys.users(params.tenantId!)],
            });

            toast.success(t("users.invite.success", { email: params.email }));
        },
        onError: () => {
            toast.error(t("users.invite.error"));
        },
    });
};
